import { calculateProductTotal } from '../../utils/platformUtils';
import { toDate, startOfPeriod, addPeriod, getPeriodNumber, periodLabel, withinPeriod } from '../../utils/dateUtils';
import { OrderHistory, EmailProductData } from '../../stores/appOrderHistoryStore';
import { OverallTypeFilter } from '../../stores/appFiltersStore';
import { resolveSpendSide } from '../../utils/spendSideUtils';

// Re-export date utilities for backward compatibility
export { toDate, startOfPeriod, addPeriod, getPeriodNumber, periodLabel, withinPeriod };

export const COLORS: Record<string, string> = {
  profilgrec: "#000000", // Profil Grec — black logo
  euroslagerijgrieving: "#89162f", // Burgundy from official GHV logo
  hollandvis: "#6a9748", // Green from the oval invoice logo
  qculinair: "#648d25", // Dominant green from the invoice logo
  binsbergen: "#f68b18", // Orange from the invoice logo
  guillaume: "#000000", // Monochrome FG logo
  notenfabriek: "#000000", // Monochrome Notenfabriek logo
  bidfood:  "#0e0149", // Bidfood — dark blue from logo
  vangelder: "#128241", // Van Gelder — green from logo
  dzw:      "#231d1f", // DZW — dark grey from logo
  gnb:      "#000000", // GNB — black from logo
  dunyong:  "#cd3232", // Dunyong — red from logo
  tonkonijn: "#ffa500", // Ton Konijn — orange from logo
  sligro:   "#006400", // Sligro — dark green from logo
  yama:     "#7532a8", // Yama — red from logo
  banketbakkerij: "#900000", // Banketbakkerij — red from logo
  otelli:     "#000000", // Otelli — black from logo
  jongvader:  "#625d5a", // Jong Vader — grey from logo
  gebogelato: "#dd6806", // Gebogelato — orange from logo
  vinvin:     "#e8dec9", // Vinvin — white wine color from logo
  kwastwijnkopers: "#9b2948", // Kwaastwijnkopers — bordeau wine color from logo
  nicetomeat: "#d0181b", // Nice to Meat — deep red from logo
  fixfisch: "#586781", // Fix Fisch — blue from logo
  rungis: "#000000", // Rungis — black from logo
  hanos: "#56004f", // Hanos — purple from logo
  rijpelaal: "#402314", // Rijpelaal — brown from logo
  zuiver: "#6cbc95", // Zuiver — green from logo
  debergsecave: "#a26624", // De Bergse Cave — brown from logo
  hyscon: "#4ca22c", // Hyscon — green from logo
  rijnmondgas: "#2cabe0", // Rijnmondgas — blue from logo
  winterhalter: "#5dcaf3", // Winterhalter — light blue from logo
  eurofins: "#094897", // Eurofins — blue from logo
  koelmonteur: "#10aebe", // Koelmonteur — blue from logo
  brandingclub: "#000000", // Branding Club — black from logo
  whiskysite: "#8B2E26", // Whiskysite — Burgundy — #8B2E26
  evides: "#5BB9C8", // Evides — Aqua / Teal-Blue — #5BB9C8
  upta: "#8A8F93", // UPTA — Grey (with cyan accent) — #8A8F93
  wintax: "#3B6C9C", // Wintax — Steel Blue — #3B6C9C
  verisure: "#E30613", // Verisure — Bright Red — #E30613
  kiya: "#62B5B5", // Kiya — Turquoise — #62B5B5
  eneco: "#E60050", // Eneco — Magenta-Red — #E60050
  fourcroy: "#000000", // Fourcroy — monochrome invoice logo
  swinkels: "#000000", // Swinkels — monochrome logo
  bourgogneexpress: "#000000", // Bourgogne Express — black wordmark
  buonappetito: "#000000", // Buon Appetito — black lettering in the logo
  lexception: "#b40832", // Lexception — Red — #b40832
  nitrobrew: "#000000", // Nitrobrew — Black — #000000
  rightspirits: "#0f3d37", // Right Spirits — Black — #000000
  vatex: "#293465", // Vatex — Blue — #293465
  sidhu: "#ff6602", // Sidhu — Orange — #ff6602
  illy: "#da251d", // Illy — red — #da251d
  degrootedelgebak: "#ac9a57", // De Groote Delgebak — Brown — #ac9a57
  europastry: "#3e4f44", // Europastry — dark green from logo background
  kaasatelier: "#fee14d", // Kaasatelier — Yellow — #fee14d
  heineken: "#00541d", // Heineken — Dark green from logo
  patisseriebondsmolders: "#000000",
  jordysbakery: "#000000", // Jordys Bakery — Black
  schmidtzeevis: "#234b91", // Schmidt Zeevis — Green
  vhcjongens: "#fec601", // VHC Jongens — yellow from logo
  grapedistrict: "#e0b22e", // Gold from the invoice logo
  staffhorst: "#f24f4f", // Staffhorst coral wordmark
  exclusivelabelbenelux: "#9c7529", // Gold from embedded invoice logo
  marckroonwijn: "#626040", // Marc Kroon Wijn — Olive green from logo
  lfe: "#844b54", // LFE — burgundy from supplied logo
  pastanobile: "#FF7600", // Pacenti / Pasta Nobile brand orange
  moestuinvollenhoven: "#195c42", // Moestuin Vollenhoven — green from supplied logo
  jansondranken: "#000000", // Janson Dranken — black logo
  lindschotendrink: "#16243e", // Lindschoten Drink — navy from logo
  foodsteps: "#c1a151", // Food Steps — brand gold from logo
  bottlebusiness: "#000000", // Bottle Business — black/white logo
  drankenhandel: "#000000", // Drankenhandel Willemstad — black (brand color)
  hoecksevis: "#babbdf", // Hoeckse Vis — brand color
  manetti: "#000000", // Manetti — black (brand color)
  zeechocolate: "#000000", // Zeechocolate (ZEE BV) — black (brand color)
  santaskoffie: "#ffae61", // Santas Koffie — orange (brand/logo color)
  ceesvannoordwijn: "#c53f58", // Wijnkoperij Cees van Noord — wine red (brand color)
  maisonroutin: "#000000", // Maison Routin (Routin 1883) — black from logo
  sanderswijnkopers: "#2a2a2a", // Sanders Wijnkopers — Dark grey from logo
  altseafood: "#01afef", // ATL Seafood — Light blue from logo
  bjdelogie: "#97c8a5", // BJ Delogie — Soft green from logo
  houseofcaviar: "#e6decc", // House of Caviar — Cream/beige from logo
  claudia: "#e30614", // Claudia — Red from logo
  dvine: "#c7848b", // D-Vine — Rosé wine color
  vlaamschbroodhuys: "#4e3325", // Vlaamsch Broodhuys — Brown from logo
  brouwerijnoordt: "#000000", // Brouwerij Noordt — black logo
  dekoekfabriek: "#efba96", // De Koekfabriek — peach from supplied logo
  minzevenijs: "#fb7e3f", // Min Zeven IJs — supplied brand color
  loui: "#e4012f", // L’OUI — red from supplied logo
  chocoseum: "#46160a", // Chocoseum — dark brown from the supplied logo
  noordmanwijnimport: "#a90938", // Noordman Wijnimport — burgundy from logo
  koektails: "#bd0c28", // Koektails — Red from logo
  frobel: "#000000", // Fröbel — Black wordmark from logo
  ijsenijskoud: "#3d92c4", // IJSenIJSkoud — Blue from logo
  tastetree: "#57a04f", // TasteTree — Green from logo
  damman: "#3B1F0B", // Damman — Dark chocolate brown
  karelvdvorm: "#871811", // Karel van der Vorm — Dark red from logo
  treuren: "#0e5d37", // Treuren — Green from logo
  linschoten: "#0f0662", // Linschoten — Dark blue from logo
  casaron: "#bf1622", // CasaRon — Red from logo
  properproducts: "#f07f02", // Proper Products — Orange from logo
  nespresso: "#000000", // Nespresso — Black from logo
  verbuntverlinden: "#c28e47", // Verbunt & Verlinden — Gold from logo
  kobier: "#000000", // Kobier — Black
  stadshavenbrouwerij: "#E8A317", // Stadshaven Brouwerij — Golden amber from brewery branding
  kemmewines: "#a2744d", // Kemme Wines — Brown from logo
  moethennessy: "#000000", // Moët Hennessy — Black
  jouwgroenteman: "#cd6900", // Jouw Groenteman — Orange from branding
  delicatezzeditalia: "#4f6940", // Delicatezze d'Italia — Green from branding
  goldtdrinks: "#FFD700", // Goldtdrinks — Gold from branding
  lamuse: "#5d3a1b", // Fromagerie L'Amuse — Brown from branding
  vetlazy: "#000000", // Vet & Lazy Brouwerij — Black
  ianenwijn: "#797e69", // Ian en Wijn — Olive green from branding
  laprosciutteria: "#8B0000", // La Prosciutteria — Dark red, Italian theme
  winespirits: "#00525c", // Wine & Spirits — Teal from branding
  lasalute: "#8aa659", // La Salute — Green from branding
  debruijn: "#665c59", // De Bruijn Wijnkopers — brand color
  vinites: "#a7a5a6", // Vinites — brand color
  bolomey: "#ab915e", // Bolomey Wijnimport — brand color
  vleck: "#8e8a7f", // Vleck Wijnen — brand color
  joka: "#826613", // Joka — brand color
  dekoningvlees: "#bf0a2e", // De Koning Vlees — brand color
  cornybakers: "#efbc00", // Corny Bakers — brand color
  gioempanadas: "#D4A05C", // Gio Empanadas — empanada dough color
  manmetbrilkoffie: "#000000", // Man Met Bril Koffie — black
  haveafew: "#014538", // Have a Few — brand color
  slagerijooteman: "#0165a7", // Slagerij Ooteman — brand color
  aapnootbrood: "#94c8ee", // Aap Noot Brood — light blue from branding
  scanwic: "#ff393b", // Scanwic Food Services — brand red
  deweert: "#477342", // VOF de Weert — brand green from logo
  kompaan: "#000000", // Kompaan — black
  icefactory: "#E0FFFF", // Icefactory — light cyan (ice)
  lindenhoff: "#000000", // Lindenhoff — black
  ebo: "#000000", // EBO van den Bor — black from logo
  freshfood_dh: "#327e2b", // Fresh Food Den Hague — green from logo
  ruyken: "#e22333", // Ruyken — red from logo
  filta: "#41a1c6", // Filta — blue from logo
  espresso_sw: "#e90000", // Espresso Service West — red from logo
  vanhatten: "#00499d", // Van Hattem Horeca — blue from logo
  aps: "#ff2128", // APS (Art of Private Shaking) — red from logo
  sebcocktails: "#000000", // SEB.Cocktails — black wordmark
  bevenco: "#3d3f40", // Bevenco — dark grey from logo
  ilovecannoli: "#a4a984", // I Love Cannoli (Di Luca Food) — sage green from logo
  horecagoedkoop: "#ffbb00", // Horecagoedkoop (Drankengilde) — amber yellow from logo
  horecaking: "#fe0d81", // Horeca King — pink/magenta from logo
  alberthein: "#01ade6", // Albert Heijn — light blue from logo
  takeaware: "#000000", // TakeAware — black from logo
  jessedelicatesse: "#1550b4", // JesseDelicatesse — blue from branding
  purematcha: "#1c4b0c", // Pure Matcha Partners — matcha green from logo
  bestbreadboba: "#2866b1", // Best Bread Boba — blue from logo
  andrekerstens: "#68311d", // André Kerstens — dark brown/maroon from logo
  filiuskaas: "#fdb805", // Filiuskaas — golden yellow brand color
  vanillaventure: "#474e43", // Vanilla Venture — dark olive/sage brand color
  greenfreshfood: "#019e73", // Green Fresh Food — green brand color
  abinbev: "#e0c216", // AB InBev — golden yellow brand color
  degoudenton: "#550f49", // De Gouden Ton — deep purple brand color
  vulcaanbier: "#c20011", // Vulcaanbier — brand red from logo
  nrbc: "#d38c63", // NRBC (New Rotterdam Brewing Collective) — brand color
  senzatea: "#1a4d3a", // Senza Tea — deep green from brand
  deklokdranken: "#004040", // De Klok Dranken — dark green from logo
  rellmanfoods: "#79b338", // Rellman Foods — leaf green from logo
  bergfood: "#b2172d", // Bergfood — brand red from logo
  cavinoir: "#857650", // Cavinoir — gold emblem/text color from logo
  pieksmanwijn: "#000000", // Pieksman Wijnimport — black/white logo, default black
  deambachterie: "#b55201", // De Ambachterie — brand orange accent from logo
  driessenfood: "#093b35", // Driessen Food — dark green from logo
  drinksfromjapan: "#82d4ce", // Drinks from Japan — teal from logo
  bemakers: "#000000", // Bemakers — black wordmark logo
  mariuswijnsake: "#37404b", // Marius Wijn & Sake — dark slate from logo wordmark
  dorstlust: "#000000", // Dorstlust — black wordmark from logo
  bartsbottles: "#000000", // Bart's Bottles — black/white logo
  bronwijn: "#232323", // Bron — near-black from logo wordmark
  janvanas: "#085d93", // Vishandel Jan van As — brand blue from the logo wordmark
  drtrading: "#000000", // D.R. Trading — black wordmark logo (no brand color)
  fullcircle: "#ef7725", // Full Circle — orange from logo
  dasbrot: "#000000", // Das Brot — black stencil wordmark from logo
  qsta: "#ef7910", // QSTA — orange wordmark from logo
  ollecobunge: "#c0d642", // Olleco Bunge — lime green teardrop from logo
  smitsdelicious: "#1d1d1b", // Smit's Delicious — black script wordmark from logo
  dudok: "#2a5536", // from logo — Dudok dark green
  westlandpeppers: "#fe6401", // from logo — Westlandpeppers orange wordmark
  kaapsebrouwers: "#f48c6c", // from logo — Kaapse Brouwers salmon/coral accent
  koekela: "#ab012e", // from logo — Koekela crimson script wordmark
  vandebakker: "#000000", // Van de Bakker — black ink illustration/script logo, no brand color
  stationbergweg: "#000000", // Station Bergweg — black wordmark from logo
  koffiekompaan: "#d73a15", // Koffiekompaan — brand red-orange half of the logo mark
  wijnhandelherman: "#6a1431", // Wijnhandel Herman — burgundy of the round HermanWines seal
  dutchwasabi: "#2b3435", // Dutch Wasabi — dark slate wordmark from the logo
  localharvest: "#4b4411", // Olive from the supplied Local Harvest logo
  jfcholland: "#ca5d24", // JFC Holland — burnt orange of the JFC wordmark
  josglas: "#ebaba4", // Jos Glas — supplied brand color
  kaasfort: "#7ea679", // Green from the supplied Kaasfort logo
  naturalia: "#18713c", // Supplied Naturalia brand color
  sakebase: "#000000", // Sakebase — black icon + wordmark from logo
  cellarmasters: "#6c1418", // Cellarmasters — deep wine red from the logo
  dronk: "#000000", // Selected by Dronk — black script wordmark from logo
  interfastfood: "#ee3b33", // Interfastfood Rotterdam — red from logo
  kraan: "#c7312a", // Kraan Vleesservice — brand red
  hicret: "#862a1a", // Hicret — dark red wordmark from logo
  koylu: "#114f53", // Köylü Bakery — teal from logo
  avo: "#e14243", // AVO-Werke — red from logo
  iranbezorger: "#e1ae4a", // Iranbezorger — gold column accent from logo
  antep: "#db241c", // Antep’s Sweet — red from logo
  peeze: "#2a2a29", // Peeze — near-black wordmark from logo
  cocktailunlimited: "#CAA163", // Cocktail Unlimited — brand gold from logo
  cookboon: "#000000", // Cook & Boon — black wordmark from logo
  vanleersum: "#000c22", // Van Leersum — navy from supplied logo
  baghrir: "#000000", // Baghrir — black wordmark from the letterhead
  lakarusch: "#974706", // Lakarusch — brown wordmark from logo
  vhckreko: "#6e2b63", // VHC Kreko — purple from the logo (the yellow is vhcjongens')
  fontijn: "#b81722", // Fontijn Vlees en Vleeswaren — red from the logo
  verhoek: "#1a1a19", // De Mannen van Verhoek — near-black from the logo
  seafoodcentre: "#243167", // Seafood Centre Scheveningen — navy from the logo
  altinpetek: "#000000", // Patisserie Altin Petek — black wordmark from the logo
  quebracho: "#000000", // Quebracho Enterprise — black logo

  ziggo: "#ff8500",
  action: "#004e96",
  google: "#33a853",
  horecaworld: "#fe7e00",
  horecarama: "#ad0015",
  lidl: "#fff000",
};

export const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2
});

export const eur = (n?: number) => currencyFormatter.format(n ?? 0);

// Filter utilities
export type CategoryFilter = 'all' | 'food' | 'non-food';

export function isFoodProduct(product: { category?: string; mainCategory?: string }): boolean {
  // Use mainCategory if available, otherwise fallback to category check for backward compatibility
  if (product.mainCategory !== undefined) {
    return product.mainCategory !== 'NON_FOOD';
  }
  // Fallback for backward compatibility: use exact category matching
  return product.category !== 'NON_FOOD';
}

/**
 * Calculate order totals (both ex VAT and incl VAT) from filtered products
 */
function calculateFilteredOrderTotals(
  products: EmailProductData[],
  platform: string
): { total: number; totalWithTaxes: number } {
  let total = 0;
  let totalWithTaxes = 0;

  products.forEach(product => {
    const productTotal = calculateProductTotal(product, platform, true);
    const productTotalWithTaxes = calculateProductTotal(product, platform, false);

    total += productTotal;
    totalWithTaxes += productTotalWithTaxes;
  });

  return { total, totalWithTaxes };
}

export function calculateOrderTotal(order: OrderHistory, categoryFilter: CategoryFilter): number {
  // If no category filter applied, use the original order total
  if (categoryFilter === 'all') {
    return order.total || 0;
  }

  // For filtered categories, calculate total from matching products
  if (!order.productData || order.productData.length === 0) {
    return 0;
  }

  const matchingProducts = order.productData.filter((product) => {
    if (categoryFilter === 'food') {
      return isFoodProduct(product);
    } else { // non-food
      return !isFoodProduct(product);
    }
  });

  return matchingProducts.reduce((total: number, product) => {
    return total + calculateProductTotal(product, order.platform);
  }, 0);
}

export function filterOrdersByCategory(orders: OrderHistory[], categoryFilter: CategoryFilter): OrderHistory[] {
  if (categoryFilter === 'all') return orders;

  return orders.map(order => {
    let filteredProducts;

    if (categoryFilter === 'food') {
      filteredProducts = order.productData?.filter((product) => isFoodProduct(product)) || [];
    } else { // non-food
      filteredProducts = order.productData?.filter((product) => !isFoodProduct(product)) || [];
    }

    // Calculate both totals from filtered products
    const { total, totalWithTaxes } = calculateFilteredOrderTotals(filteredProducts, order.platform);

    return {
      ...order,
      productData: filteredProducts,
      total,
      totalWithTaxes
    };
  }).filter(order => order.productData.length > 0); // Remove orders with no matching products
}

export function filterOrdersByPlatforms(orders: OrderHistory[], platformFilters: string[]): OrderHistory[] {
  if (platformFilters.length === 0) return orders;

  return orders.filter(order => {
    const platform = String(order.platform).toLowerCase();

    // For any_invoice orders, match against unknownSupplierName
    if (platform === 'any_invoice' && order.unknownSupplierName) {
      return platformFilters.includes(order.unknownSupplierName.toLowerCase());
    }

    // For all other platforms, match against platform name
    return platformFilters.includes(platform);
  });
}

export function filterOrdersBySpecificCategories(orders: OrderHistory[], categoryFilters: string[]): OrderHistory[] {
  if (categoryFilters.length === 0) return orders;

  return orders.map(order => {
    const filteredProducts = order.productData?.filter((product) => {
      return product.category && categoryFilters.includes(product.category);
    }) || [];

    // Calculate both totals from filtered products
    const { total, totalWithTaxes } = calculateFilteredOrderTotals(filteredProducts, order.platform);

    return {
      ...order,
      productData: filteredProducts,
      total,
      totalWithTaxes
    };
  }).filter(order => order.productData.length > 0); // Remove orders with no matching products
}

export function filterOrdersBySubCategories(orders: OrderHistory[], subCategoryFilters: string[]): OrderHistory[] {
  if (subCategoryFilters.length === 0) return orders;

  return orders.map(order => {
    const filteredProducts = order.productData?.filter((product) => {
      // Product must have at least one of the selected subcategories
      if (!product.subCategories || product.subCategories.length === 0) return false;
      return product.subCategories.some(subCat => subCategoryFilters.includes(subCat));
    }) || [];

    // Calculate both totals from filtered products
    const { total, totalWithTaxes } = calculateFilteredOrderTotals(filteredProducts, order.platform);

    return {
      ...order,
      productData: filteredProducts,
      total,
      totalWithTaxes
    };
  }).filter(order => order.productData.length > 0); // Remove orders with no matching products
}

export function filterOrdersByRestaurantSections(orders: OrderHistory[], sectionFilters: string[]): OrderHistory[] {
  if (sectionFilters.length === 0) return orders;

  const filteredOrders = orders.filter(order => {
    return order.restaurantSection && sectionFilters.includes(order.restaurantSection.id);
  });
  return filteredOrders;
}

/**
 * Filter orders by overall type (all/food/drinks/non-food)
 * This is a high-level filter that categorizes orders into food vs drinks vs non-food.
 * The whole decision lives in `resolveSpendSide` — 'food' | 'drinks' | 'non-food' are exactly its
 * return values, so a filter is a straight equality on the resolved side. With the connection's
 * `combinedOrderingAccount` off (everyone by default) that is the section-type behaviour this
 * function has always had; on, each product line is placed by its own mainCategory.
 * Spec: z_ralph/tasks/1_new_tasks/spec-combined-ordering-accounts.md §6
 */
export function filterOrdersByOverallType(orders: OrderHistory[], overallTypeFilter: OverallTypeFilter): OrderHistory[] {
  if (overallTypeFilter === 'all') return orders;

  return orders.map(order => {
    let filteredProducts: EmailProductData[];

    if (overallTypeFilter === 'food' || overallTypeFilter === 'drinks' || overallTypeFilter === 'non-food') {
      const sectionType = order.restaurantSection?.restaurantSectionType;
      const combined = order.restaurantSection?.combinedOrderingAccount === true;
      filteredProducts = order.productData?.filter(
        product => resolveSpendSide(product, sectionType, combined) === overallTypeFilter
      ) || [];
    } else {
      // For any other filter values, return empty array (should not happen)
      filteredProducts = [];
    }

    // Calculate both totals from filtered products
    const { total, totalWithTaxes } = calculateFilteredOrderTotals(filteredProducts, order.platform);

    return {
      ...order,
      productData: filteredProducts,
      total,
      totalWithTaxes
    };
  }).filter(order => order.productData.length > 0); // Remove orders with no matching products
}

export function filterOrdersByRestaurants(
  orders: OrderHistory[],
  restaurantFilters: string[],
  platformToSectionIndex?: Record<string, any>
): OrderHistory[] {
  if (restaurantFilters.length === 0) return orders;

  return orders.filter(order => {
    // If we have the platform-to-section index, use it to find the restaurant
    if (platformToSectionIndex && order.restaurantPlatformId) {
      const section = platformToSectionIndex[order.restaurantPlatformId];
      if (section && section.restaurantId) {
        return restaurantFilters.includes(section.restaurantId);
      }
    }

    // Fallback to direct restaurantId if available
    return order.restaurantId && restaurantFilters.includes(order.restaurantId);
  });
}

// Helper function to extract available restaurants from order data
export function getAvailableRestaurants(orderData: OrderHistory[]): { id: string; name: string }[] {
  const restaurantMap = new Map<string, {id: string; name: string}>();

  orderData.forEach(o => {
    if (o.restaurantId && o.restaurantName) {
      restaurantMap.set(o.restaurantId, { id: o.restaurantId, name: o.restaurantName });
    }
  });

  const restaurants: { id: string; name: string }[] = [];
  restaurantMap.forEach(r => restaurants.push(r));
  return restaurants.sort((a, b) => a.name.localeCompare(b.name));
}

// Helper function to extract available sections from order data
export function getAvailableSections(orderData: OrderHistory[]): { id: string; name: string }[] {
  const sectionMap = new Map<string, {id: string; name: string}>();

  orderData.forEach(o => {
    if (o.restaurantSection) {
      sectionMap.set(o.restaurantSection.id, { id: o.restaurantSection.id, name: o.restaurantSection.name });
    }
  });

  const sections: { id: string; name: string }[] = [];
  sectionMap.forEach(s => sections.push(s));
  return sections.sort((a, b) => a.name.localeCompare(b.name));
}

// Helper function to extract available platforms from order data
// For any_invoice orders, use the unknownSupplierName instead of the platform
export function getAvailablePlatforms(orderData: OrderHistory[]): string[] {
  const platformSet = new Set<string>();

  orderData.forEach(order => {
    const platform = String(order.platform).toLowerCase();

    // For any_invoice, add the unknownSupplierName if it exists
    if (platform === 'any_invoice' && order.unknownSupplierName) {
      platformSet.add(order.unknownSupplierName.toLowerCase());
    } else {
      // For all other platforms, add the platform name
      platformSet.add(platform);
    }
  });

  return Array.from(platformSet).sort();
}

// Helper function to extract available categories from order data
export function getAvailableCategories(orderData: OrderHistory[]): string[] {
  return Array.from(new Set(
    orderData
      .flatMap(o => o.productData || [])
      .map(p => p.category)
      .filter((category): category is string => Boolean(category))
  )).sort();
}
// Main category options
export const MAIN_CATEGORIES = [
  { value: 'FOOD', label: 'Food' },
  { value: 'DRINKS', label: 'Drinks' },
  { value: 'NON_FOOD', label: 'Non-Food' },
] as const;

// FOOD categories
export const FOOD_CATEGORIES = [
  'MEAT_POULTRY',
  'SEAFOOD',
  'EGGS_AND_DAIRY',
  'FRUITS_AND_VEGETABLES',
  'OILS_AND_FATS',
  'GRAINS_AND_STARCHES',
  'DRY_GOODS',
  'CANNED_AND_PRESERVED_GOODS',
  'CONDIMENTS_AND_SAUCES',
  'BAKERY_AND_PASTRY',
  'OTHER',
] as const;

// DRINKS categories
export const DRINKS_CATEGORIES = [
  'WINE',
  'BEER',
  'SPIRITS',
  'COCKTAILS',
  'COLD_DRINKS',
  'WATER',
  'HOT_BEVERAGES',
  'BAR_INGREDIENTS',
  'OTHER',
] as const;

// NON_FOOD categories
export const NON_FOOD_CATEGORIES = [
  'CLEANING_AND_HYGIENE',
  'DISPOSABLES_AND_PACKAGING',
  'KITCHEN_CONSUMABLES',
  'BAR_CONSUMABLES',
  'OFFICE_AND_ADMIN',
  'OTHER',
] as const;

// Subcategories by mainCategory and category
// Note: FOOD subcategories (ASIAN, FROZEN) can apply to ANY food category
// DRINKS subcategories are category-specific
export const SUB_CATEGORIES_BY_MAIN_CATEGORY_AND_CATEGORY: Record<string, Record<string, string[]>> = {
  FOOD: {
    // All FOOD categories can have ASIAN and FROZEN subcategories
    'MEAT_POULTRY': ['ASIAN', 'FROZEN'],
    'SEAFOOD': ['ASIAN', 'FROZEN'],
    'EGGS_AND_DAIRY': ['ASIAN', 'FROZEN'],
    'FRUITS_AND_VEGETABLES': ['ASIAN', 'FROZEN'],
    'OILS_AND_FATS': ['ASIAN', 'FROZEN'],
    'GRAINS_AND_STARCHES': ['ASIAN', 'FROZEN'],
    'DRY_GOODS': ['ASIAN', 'FROZEN'],
    'CANNED_AND_PRESERVED_GOODS': ['ASIAN', 'FROZEN'],
    'CONDIMENTS_AND_SAUCES': ['ASIAN', 'FROZEN'],
    'BAKERY_AND_PASTRY': ['ASIAN', 'FROZEN'],
    'OTHER': ['ASIAN', 'FROZEN'],
  },
  DRINKS: {
    // DRINKS subcategories are category-specific
    'WINE': ['RED_WINE', 'WHITE_WINE', 'ROSE_WINE', 'BUBBLES'],
    'BEER': [],
    'SPIRITS': [],
    'COCKTAILS': [],
    'COLD_DRINKS': ['SOFT_DRINK', 'JUICE'],
    'WATER': ['STILL', 'SPARKLING'],
    'HOT_BEVERAGES': ['COFFEE', 'TEA'],
    'BAR_INGREDIENTS': [],
    'OTHER': [],
  },
  NON_FOOD: {
    // NON_FOOD has no subcategories
    'CLEANING_AND_HYGIENE': [],
    'DISPOSABLES_AND_PACKAGING': [],
    'KITCHEN_CONSUMABLES': [],
    'BAR_CONSUMABLES': [],
    'OFFICE_AND_ADMIN': [],
    'OTHER': [],
  },
};

// Helper function to determine main category from a category
function getMainCategoryFromCategory(category: string): string | null {
  if (FOOD_CATEGORIES.includes(category as any)) return 'FOOD';
  if (DRINKS_CATEGORIES.includes(category as any)) return 'DRINKS';
  if (NON_FOOD_CATEGORIES.includes(category as any)) return 'NON_FOOD';
  return null;
}

// Helper function to extract available subcategories from order data
export function getAvailableSubCategories(orderData: OrderHistory[]): string[] {
  return Array.from(new Set(
    orderData
      .flatMap(o => o.productData || [])
      .flatMap(p => p.subCategories || [])
      .filter((subCategory): subCategory is string => Boolean(subCategory))
  )).sort();
}

/**
 * Filter subcategories based on selected categories and available data
 * Returns only subcategories that:
 * 1. Are valid for the selected categories (based on category-to-subcategory mapping)
 * 2. Actually exist in the filtered order data
 */
export function getFilteredSubCategories(
  selectedCategories: string[],
  allAvailableSubCategories: string[],
  orderData: OrderHistory[]
): string[] {
  // If no categories selected, return all available subcategories
  if (selectedCategories.length === 0) {
    return allAvailableSubCategories;
  }

  // Get valid subcategories for selected categories
  const validSubCategories = new Set<string>();
  
  selectedCategories.forEach(category => {
    const mainCategory = getMainCategoryFromCategory(category);
    if (mainCategory) {
      const subCats = SUB_CATEGORIES_BY_MAIN_CATEGORY_AND_CATEGORY[mainCategory]?.[category] || [];
      subCats.forEach(subCat => validSubCategories.add(subCat));
    }
  });

  // Get subcategories that actually exist in the data for selected categories
  const subCategoriesInData = new Set<string>();
  orderData.forEach(order => {
    order.productData?.forEach(product => {
      // Only include if product's category is in selected categories
      if (product.category && selectedCategories.includes(product.category)) {
        product.subCategories?.forEach(subCat => {
          if (validSubCategories.has(subCat)) {
            subCategoriesInData.add(subCat);
          }
        });
      }
    });
  });

  // Return intersection: valid subcategories that exist in data
  return allAvailableSubCategories.filter(subCat => 
    validSubCategories.has(subCat) && subCategoriesInData.has(subCat)
  ).sort();
}

// Column width constants for products page responsive design
export const COLUMN_WIDTHS = {
  priceAlert: "w-14 sm:w-16 lg:w-20",
  price: "w-32 sm:w-36 lg:w-44",
  unitPrice: "w-20 lg:w-28",
  quantity: "w-20 lg:w-28",
  totalSpend: "w-22 lg:w-28"
} as const;
